import React, { FC } from 'react';
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useCartModelDisplay from '../../../../hooks/useCartModelDisplay';
import { ChassisModelTypes } from '../../../../@types/ChassisType';
import useCurrencyConverter from '../../../../hooks/useCurrencyConverter';

interface CartInfoBarProps {
  hideAllPrices: boolean;
}

const CartInfoBar: FC<CartInfoBarProps> = ({ hideAllPrices }) => {
  const { chassisType } = useCartModelDisplay();
  const { watch } = useFormContext();
  const total = watch('total');
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop'));
  const { convertCurrency } = useCurrencyConverter();

  const openBrochurePage = () => {
    switch (chassisType?.model) {
      case ChassisModelTypes.X2:
        window.open('https://madjax.com/wp-content/uploads/2024/08/X2-salessheet.pdf', '_blank');
        break;
      case ChassisModelTypes.X4:
        window.open('https://madjax.com/wp-content/uploads/2024/08/X4-salessheet.pdf', '_blank');
        break;
      case ChassisModelTypes.X6:
        window.open('https://madjax.com/wp-content/uploads/2024/08/X6-salessheet.pdf', '_blank');
        break;
      case ChassisModelTypes.E4:
        window.open('https://madjax.com/wp-content/uploads/2024/08/E4-salessheet.pdf', '_blank');
        break;
    }
  }

  return (
    <Stack
      position="fixed"
      bottom="0"
      right="0"
      width="100%"
      direction="row"
      alignItems="center"
      justifyContent="space-evenly"
      flexWrap="wrap"
      zIndex={1033}
      useFlexGap
      spacing={2}
      sx={(theme) => ({
        minHeight: '100px',
        backgroundColor: '#D9D9D9',
        [theme.breakpoints.down('laptop')]: {
          minHeight: '75px',
          backgroundColor: '#444444',
          justifyContent: "center",
          alignItems: "center",
        }
      })}>
      {!hideAllPrices &&
        <Stack
          direction="column"
          height="100%"
          alignItems="center"
          sx={(theme) => ({
            [theme.breakpoints.down('laptop')]: {
              width: 'fit-content',
              minWidth: '270px'
            }
          })}>
          <Stack
            direction="row"
            spacing={1}>
            <Typography
              variant="primaryFont"
              sx={(theme) => ({
                fontSize: "38px",
                [theme.breakpoints.down('laptop')]: {
                  color: 'white',
                  fontSize: '18px',
                }
              })}>
              Total Build:
            </Typography>
            <Typography
              variant="primaryFont"
              fontWeight="bold"
              sx={(theme) => ({
                fontSize: "38px",
                [theme.breakpoints.down('laptop')]: {
                  color: 'white',
                  fontSize: '18px',
                }
              })}>
              {convertCurrency(total)}*
            </Typography>
          </Stack>
          <Typography
            variant="primaryFont"
            fontWeight="bold"
            sx={(theme) => ({
              fontSize: "12px",
              [theme.breakpoints.down('laptop')]: {
                display: 'none',
                color: 'white',
                fontSize: '10px',
              }
            })}>
            * Price does not include taxes, dealer prep, delivery, or other fees.
          </Typography>
        </Stack>
      }
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        spacing={2}
        height="100%"
        alignItems="center"
        justifyContent="center"
        sx={(theme) => ({
          '& .MuiButton-root': {
            width: '260px',
            height: '48px',
            color: 'white',
            borderRadius: 0,
            [theme.breakpoints.down('laptop')]: {
              width: 'fit-content',
              paddingLeft: 2,
              paddingRight: 2,
              backgroundColor: 'white',
              color: 'black',
              borderRadius: '10px'
            }
          },
        })}>
        <Button
          onClick={openBrochurePage}
          sx={(theme) => ({
            backgroundColor: '#323232',
            '&:hover': {
              backgroundColor: '#474747'
            }
          })}>
          <FileDownloadIcon
            sx={(theme) => ({
              display: 'none',
              marginRight: '8px',
              [theme.breakpoints.down('laptop')]: {
                display: 'block',
              }
            })} />
          {!isLaptop &&
            <Typography variant="primaryFont">
              Download Brochure
            </Typography>
          }
          {isLaptop &&
            <Typography variant="primaryFont">
              Brochure
            </Typography>
          }
        </Button>
        {(process.env.REACT_APP_DEFAULT_COUNTRY === 'US' || process.env.REACT_APP_DEFAULT_COUNTRY === 'CA') &&
          <Button
            type="submit"
            sx={(theme) => ({
              backgroundColor: '#D82727',
              '&:hover': {
                backgroundColor: '#D84747'
              }
            })}>
            <SaveIcon
              sx={(theme) => ({
                display: 'none',
                marginRight: '8px',
                [theme.breakpoints.down('laptop')]: {
                  display: 'block',
                }
              })} />
            {!isLaptop &&
              <Typography variant="primaryFont">
                Save My Build
              </Typography>
            }
            {isLaptop &&
              <Typography variant="primaryFont">
                Save
              </Typography>
            }
          </Button>
        }
      </Stack>
    </Stack>
  );
}

export default CartInfoBar;
